import { useState, type ReactNode } from "react";
import { ListFilter, X, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FilterChip } from "./FilterChip";

/** A filter type the bar can show in its "Add filter" menu and as a chip. */
export interface FilterDefinition {
  /** Stable key identifying the filter. */
  key: string;
  /** Display label shown in the menu and on the chip. */
  label: string;
  /** Icon shown in the menu and on the chip. */
  icon: LucideIcon;
  /** Summary of the current selection, or null when nothing is selected. */
  summary: string | null;
  /** The value-editing dropdown content rendered inside the chip. */
  content: ReactNode;
}

interface FilterBarProps {
  /** All available filter types, in display order. */
  filters: FilterDefinition[];
  /** Keys that should appear as chips on first render. */
  initialActiveKeys: string[];
  /** Called when a chip is removed so the owner can clear that filter's value. */
  onRemove: (key: string) => void;
  /** Called when "Clear filters" is clicked so the owner can reset all values. */
  onClearAll: () => void;
}

/**
 * A generic filter bar: an "Add filter" dropdown of filter types, each added one
 * becoming a chip whose own dropdown edits its value, plus a "Clear filters"
 * action. Chip order and open state are managed internally; value state lives
 * with the owner via each definition's `content`/`summary` and the callbacks.
 * @param filters - All available filter types, in display order
 * @param initialActiveKeys - Keys to show as chips on first render
 * @param onRemove - Called with a key when its chip is removed
 * @param onClearAll - Called when all filters are cleared
 * @returns The filter bar
 */
export function FilterBar({
  filters,
  initialActiveKeys,
  onRemove,
  onClearAll,
}: FilterBarProps) {
  const [chipOrder, setChipOrder] = useState<string[]>(initialActiveKeys);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const inactiveTypes = filters.filter((type) => !chipOrder.includes(type.key));

  const addFilter = (key: string) => {
    setChipOrder((order) => [...order, key]);
    setAddOpen(false);
    setOpenKey(key);
  };

  const removeFilter = (key: string) => {
    setChipOrder((order) => order.filter((k) => k !== key));
    if (openKey === key) {
      setOpenKey(null);
    }
    onRemove(key);
  };

  const clearAll = () => {
    setChipOrder([]);
    setOpenKey(null);
    onClearAll();
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={addOpen} onOpenChange={setAddOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 border-dashed"
            disabled={inactiveTypes.length === 0}
          >
            <ListFilter className="h-3.5 w-3.5" />
            Add filter
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search filters..." />
            <CommandList>
              <CommandEmpty>No filters found.</CommandEmpty>
              <CommandGroup heading="General">
                {inactiveTypes.map((type) => (
                  <CommandItem
                    key={type.key}
                    value={type.label}
                    onSelect={() => addFilter(type.key)}
                    className="gap-2"
                  >
                    <type.icon className="h-4 w-4 text-muted-foreground" />
                    {type.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {chipOrder.map((key) => {
        const type = filters.find((t) => t.key === key);
        if (!type) {
          return null;
        }
        return (
          <FilterChip
            key={key}
            icon={type.icon}
            label={type.label}
            summary={type.summary}
            open={openKey === key}
            onOpenChange={(open) => setOpenKey(open ? key : null)}
            onRemove={() => removeFilter(key)}
          >
            {type.content}
          </FilterChip>
        );
      })}

      {chipOrder.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-destructive hover:text-destructive"
          onClick={clearAll}
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
